// ─────────────────────────────────────────────────────────────────────────────
// ÓRDENES DE SERVICIO + SOPORTE — PERSISTIDAS EN EL BACKEND (Postgres, por el hub-api).
//
// Mismo patrón que cotizaciones/entitlements: la orden vive en la base de Solar Hub
// (`service_orders`, workflow `esquema/ordenes.json`) y el localStorage `al:ordenes` queda
// como CACHÉ/offline que se HIDRATA desde el backend → CROSS-DEVICE:
//   - el cliente CONSULTA el estado de SU orden desde cualquier equipo (por correo);
//   - el gestor asigna técnico / avanza el estado y el cliente lo VE;
//   - el cliente abre un soporte y el gestor lo VE en su cola.
// La llave es el `id` generado en cliente (os_xxx) → upsert idempotente. Toda mutación
// (crear, avanzar estado, asignar técnico, ticket) hace `pushOrden(order)` con el objeto
// COMPLETO → el backend reescribe la fila (columnas + payload snapshot). Si no hay backend
// configurado, cae a la caché local (degradación elegante, sin romper la UI).
// ─────────────────────────────────────────────────────────────────────────────

import { hubPost, hubConfigurado } from './hub';
import { tokenParaBackend } from './adminAuth';

// 🔑 Prefiere el token de ADMINISTRACIÓN (el único FIRMADO por el hub-api) y cae al de la suite.
// Desde que `exigeAdmin` falla cerrado (2026-08-29), el `alg:none` que acuña `mintToken` da 401 en
// las ops de administración: mandarlo sería dejar el panel del dueño muerto después de que se
// provisione el admin.
const sessionToken = () => tokenParaBackend();
const norm = (e) => (e || '').toLowerCase().trim();

export const ordenesBackendConfigured = () => hubConfigurado();

/**
 * Reconstruye la orden del front desde la fila del backend. Las COLUMNAS son la verdad
 * de los campos mutables (estado/técnico/garantía/soporte — última actualización); el
 * `payload` aporta el snapshot original (tipos numéricos correctos de kwp/valor).
 */
export function mapRemoteOrden(row) {
  if (!row || !row.id) return null;
  const base = row.payload && typeof row.payload === 'object' ? row.payload : {};
  const num = (a, b) => (a != null && a !== '' ? Number(a) : (b != null ? b : null));
  return {
    ...base,
    id: row.id,
    quoteId: row.quoteId ?? base.quoteId ?? null,
    tipo: row.tipo || base.tipo || 'instalacion',
    cliente: row.cliente ?? base.cliente ?? '—',
    email: norm(row.email ?? base.email),
    sistema: row.sistema ?? base.sistema ?? '—',
    kwp: base.kwp ?? num(row.kwp),
    inversion: base.inversion ?? num(row.inversion),
    valor: base.valor ?? num(row.valor),
    direccion: row.direccion ?? base.direccion ?? '',
    tecnicoId: row.tecnicoId ?? base.tecnicoId ?? null,
    tecnicoNombre: row.tecnicoNombre ?? base.tecnicoNombre ?? '',
    tecnicoEmail: row.tecnicoEmail ?? base.tecnicoEmail ?? '',
    estado: row.estado || base.estado || 'programada',
    garantia: row.garantia ?? base.garantia ?? 'vigente',
    notas: row.notas ?? base.notas ?? '',
    soporte: Array.isArray(row.soporte) ? row.soporte : (Array.isArray(base.soporte) ? base.soporte : []),
    fecha: row.fecha || base.fecha || null,
    _remote: true,
  };
}

/** Persiste (crea/actualiza) la orden COMPLETA en el backend. Best-effort (optimista en UI). */
export function pushOrden(order) {
  if (!order || !order.id || !order.email || !hubConfigurado()) return;
  hubPost('ordenes', { op: 'upsert', order, token: sessionToken() })
    .catch(() => { /* best-effort: la caché local ya refleja el cambio */ });
}

/** Trae las órdenes de UN cliente (por correo) desde el backend. Devuelve array mapeado o null. */
export async function fetchMisOrdenes(email) {
  const e = norm(email);
  if (!e || !hubConfigurado()) return null;
  try {
    const r = await hubPost('ordenes', { op: 'mine', email: e, token: sessionToken() });
    if (!r || !r.ok || !Array.isArray(r.ordenes)) return null;
    return r.ordenes.map(mapRemoteOrden).filter(Boolean);
  } catch { return null; }
}

/** Trae TODAS las órdenes (gestor/admin) desde el backend. Devuelve array mapeado o null. */
export async function fetchAllOrdenes() {
  if (!hubConfigurado()) return null;
  try {
    const r = await hubPost('ordenes', { op: 'list', token: sessionToken() });
    if (!r || !r.ok || !Array.isArray(r.ordenes)) return null;
    return r.ordenes.map(mapRemoteOrden).filter(Boolean);
  } catch { return null; }
}
